import { Colors, Heading, Icon, Text } from '@amelio/ui-library';

import type { ReviewAnswerCommentThreads } from '@/types/review/CommentThread';

import { ReviewType } from '@/constants/review/CycleInstance';
import { isAnonymousFeedback } from '@/constants/review/FeedbackPrivacy';
import { useAmelioTranslations } from '@/hooks';
import { isolateDirection } from '@/utils/bidi';

interface CommentThreadHeaderProps {
    evaluation: ReviewAnswerCommentThreads;
}

/**
 * Whose feedback this is, and, for a 360, how the respondent's replies are signed: a self-evaluation
 * has no mode to tell of.
 */
export const CommentThreadHeader = ({ evaluation }: CommentThreadHeaderProps) => {
    const { t } = useAmelioTranslations();
    const isAnonymous = isAnonymousFeedback(evaluation.feedbackPrivacy);

    const titleOf = (): string => {
        if (evaluation.reviewType === ReviewType.SELF_EVALUATION) {
            return t('reviewAnswer.commentThread.titleSelfEvaluation');
        }

        if (!evaluation.revieweeName) {
            return t('reviewAnswer.commentThread.title360WithoutName');
        }

        return t('reviewAnswer.commentThread.title360', {
            name: isolateDirection(evaluation.revieweeName),
        });
    };

    return (
        <header className={'flex flex-col gap-2'}>
            <Heading
                level={1}
                weight={'semibold'}
                color={Colors.BASE}
                className={'text-2xl'}
            >
                {titleOf()}
            </Heading>

            {evaluation.reviewType === ReviewType.EVALUATION_360 && (
                <div
                    className={'flex items-start gap-2'}
                    data-testid={'comment-thread-signature-mode'}
                >
                    <Icon
                        name={isAnonymous ? 'security-lock' : 'users-user'}
                        color={Colors.BASE_600}
                        size={'sm'}
                        className={'mt-0.5 shrink-0'}
                        ariaHidden
                    />

                    <Text color={Colors.BASE_600}>
                        {isAnonymous
                            ? t('reviewAnswer.commentThread.signedAnonymously', {
                                  respondent: t('answer.feedbackPrivacy.respondent'),
                              })
                            : t('reviewAnswer.commentThread.signedWithName')}
                    </Text>
                </div>
            )}
        </header>
    );
};
